import {
  kgToLb, toDisplayHundredths, fromHundredths,
} from "../../utils/weight";

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => (
  { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
));

// The Noblesse Trading tally sheet, reproduced from the paper form.

const PER_ROW = 10;
const MIN_ROWS = 16; // the paper form always shows this many, blank or not

// The tally is written to two decimal places, and every figure on it is rounded per
// box BEFORE anything is added up — see utils/weight.js.
const cents = (s) => Number(toDisplayHundredths(s ?? "0"));
const show = (n) => fromHundredths(n);

const printWeightManifest = ({
  lotNumber, date, vendor, shipTo, billOfLading, itemDescription,
  scans = [], assembledBy = "", checkedBy = "", memo = "",
} = {}) => {
  const win = window.open("", "_blank");
  if (!win) return;

  // A rejected scan was never recorded, a duplicate is one box scanned twice, and a
  // voided one was taken off the tally on purpose.
  const OFF_THE_TALLY = new Set(["rejected", "duplicate", "voided"]);
  const boxes = scans.filter((s) => !OFF_THE_TALLY.has(s.status));

  // A tally is always in pounds.
  const inKg = (s) => String(s.weightUnit || "").toUpperCase() === "KG";
  const weightOf = (s) => {
    if (s.displayWeight) return s.displayWeight;      // already converted
    return inKg(s) ? kgToLb(s.weight) : s.weight;
  };

  const unit = "LB";
  // Flagged on the row, or still carrying a kilogram unit because it predates
  // the conversion — either way the operator should see it was converted.
  const convertedCount = boxes.filter((s) => s.convertedFrom || inKg(s)).length;
  // Boxes added in a batch at a nominal label weight, not individually weighed.
  const estimated = boxes.filter((s) => s.isEstimated);
  const estimatedTotal = estimated.reduce((acc, s) => acc + cents(weightOf(s)), 0);

  const rows = [];
  for (let i = 0; i < boxes.length; i += PER_ROW) rows.push(boxes.slice(i, i + PER_ROW));

  let grand = 0;
  const rowHtml = [];
  for (let r = 0; r < Math.max(MIN_ROWS, rows.length); r += 1) {
    const row = rows[r] || [];
    const sum = row.reduce((acc, s) => acc + cents(weightOf(s)), 0);
    grand += sum;

    const cells = Array.from({ length: PER_ROW }, (_, c) => {
      const s = row[c];
      // Nominal weights are NOT marked in the cell.
      return `<td class="w">${s ? esc(show(cents(weightOf(s)))) : ""}</td>`;
    }).join("");

    rowHtml.push(
      `<tr>
         <td class="cnt">${row.length}</td>
         ${cells}
         <td class="rowtot">${row.length ? esc(show(sum)) : "0"}</td>
       </tr>`
    );
  }

  const html = `
    <html>
      <head>
        <title>Tally${lotNumber ? " " + esc(lotNumber) : ""}</title>
        <style>
          /* Extra headroom at the top so the title is not tight against the
             sheet edge, and printers that clip the first few millimetres do
             not eat into it. */
          @page { size: letter portrait; margin: 22mm 12mm 12mm; }
          html, body {
            margin: 0; padding: 0;
            font-family: Calibri, "Segoe UI", Arial, Helvetica, sans-serif;
            color: #000; font-size: 11px;
          }
          .sheet { max-width: 760px; margin: 0 auto; }
          h1 {
            text-align: center; font-size: 26px; font-style: italic; font-weight: 700;
            margin: 18px 0 22px;
          }
          table.head { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
          table.head td { padding: 2px 4px; font-size: 12px; vertical-align: bottom; }
          .lbl { width: 90px; white-space: nowrap; }
          /* One line, but wider than 90px at the 12px the other labels use, and
             .lbl is nowrap — at full size it would push into the value cell. */
          .lbl-md { font-size: 10px; }
          .val { border-bottom: 1px solid #000; text-align: center; }
          .val-r { border-bottom: 1px solid #000; text-align: center; width: 170px; }

          table.grid { width: 100%; border-collapse: collapse; border: 1.5px solid #000; }
          table.grid td, table.grid th { border: 1px solid #000; }
          .desc { position: relative; padding: 4px 6px; font-size: 12px; text-align: center; }
          .desc-lbl { position: absolute; left: 6px; top: 4px; white-space: nowrap; }
          .desc-val { display: inline-block; font-weight: 600; }
          th.h { padding: 3px; font-size: 11px; font-weight: 400; text-align: center; }
          td.cnt { width: 52px; text-align: right; padding: 7px 6px; }
          td.w {
            width: 8.4%; text-align: right; padding: 7px 5px;
            border-left: 1px dotted #555; border-right: 1px dotted #555;
            font-variant-numeric: tabular-nums;
          }
          td.rowtot {
            width: 62px; text-align: right; padding: 7px 6px;
            font-variant-numeric: tabular-nums;
          }
          /* The two figures anyone checking this sheet looks for first, so the
             numbers are set well above the body text and the labels stay small
             enough not to compete with them. */
          td.foot { padding: 8px 6px; font-size: 12px; font-weight: 700; }
          td.foot-r { padding: 8px 6px; font-size: 12px; font-weight: 700; text-align: right; }
          .foot-n { font-size: 19px; font-variant-numeric: tabular-nums; }

          table.sign { width: 100%; border-collapse: collapse; margin-top: 16px; }
          table.sign td { padding: 10px 4px 2px; font-size: 11px; border-bottom: 1px solid #000; }
          table.sign td.gap { border-bottom: none; width: 30px; }
          .memo { margin-top: 16px; font-size: 11px; border-bottom: 1px solid #000; padding-bottom: 12px; }
          .no-print { text-align: center; margin: 16px 0; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="sheet">
          <h1>Noblesse Trading</h1>

          <table class="head">
            <tr>
              <td class="lbl">Vendor</td>
              <td class="val">${esc(vendor || "")}</td>
              <td class="lbl" style="width:50px">Date</td>
              <td class="val-r">${esc(date || "")}</td>
            </tr>
            <tr>
              <td class="lbl lbl-md">Vendor Lot #/IC#</td>
              <td class="val">${esc(shipTo || billOfLading || "")}</td>
              <td class="lbl" style="width:50px">Lot#</td>
              <td class="val-r">${esc(lotNumber || "")}</td>
            </tr>
          </table>

          <table class="grid">
            <tr>
              <td class="desc" colspan="12">
                <span class="desc-lbl">Item Description:</span>
                <span class="desc-val">${esc(itemDescription || "")}</span>
              </td>
            </tr>
            <tr>
              <th class="h">Box/Pcs.</th>
              <th class="h" colspan="${PER_ROW}">Item</th>
              <th class="h">Total</th>
            </tr>
            ${rowHtml.join("")}
            <tr>
              <td class="foot" colspan="6">Total Boxes:&nbsp;&nbsp;<span class="foot-n">${boxes.length}</span></td>
              <td class="foot-r" colspan="6">Subtotal:&nbsp;&nbsp;<span class="foot-n">${show(grand)} ${unit}</span></td>
            </tr>
          </table>

          ${estimated.length
            ? `<div style="margin-top:6px;font-size:10px;">${estimated.length} of these ${
                boxes.length} boxes carry a nominal label weight and were not weighed${
                ""} — ${esc(show(estimatedTotal))} ${unit} of the total.</div>`
            : ""}
          ${convertedCount
            ? `<div style="margin-top:6px;font-size:10px;">${convertedCount} box${
                convertedCount === 1 ? "" : "es"} labelled in kilograms, converted to pounds.</div>`
            : ""}

          <table class="sign">
            <tr>
              <td>ASSEMBLED BY${assembledBy ? "&nbsp;&nbsp;" + esc(assembledBy) : ""}</td>
              <td class="gap"></td>
              <td>CHECKED BY${checkedBy ? "&nbsp;&nbsp;" + esc(checkedBy) : ""}</td>
            </tr>
          </table>

          <div class="memo">MEMO:&nbsp;&nbsp;${esc(memo || "")}</div>

          <div class="no-print">
            <button onclick="window.print()" style="padding:8px 18px;font-size:14px;cursor:pointer;">
              Print
            </button>
          </div>
        </div>
      </body>
    </html>`;

  win.document.write(html);
  win.document.close();
};

export default printWeightManifest;
